/**
 * Hero Scene Settings Management
 * Stores and retrieves the 2 listing IDs to display in hero scene
 * Uses database for reliable persistence
 */

import logger from '../utils/logger';

class HeroSceneSettings {
  constructor() {
    this.leftListingId = null;
    this.rightListingId = null;
    this.loading = false;
  }

  /**
   * Load settings from database
   * @returns {Promise<void>}
   */
  async load() {
    try {
      this.loading = true;
      const isDev =
        typeof window !== 'undefined' &&
        (process.env.NODE_ENV === 'development' ||
          window.location.hostname === 'localhost' ||
          window.location.port === '8888');

      const endpoint = isDev
        ? '/.netlify/functions/local-db?action=getHeroSettings'
        : '/api/index.php/hero-settings';

      const response = await fetch(endpoint);

      if (!response.ok) {
        logger.log('📂 No hero scene settings found in database');
        return;
      }

      const data = await response.json();
      const payload = data && (data.data || data); // API returns {data:{...}} in prod

      if (payload && payload.leftListingId && payload.rightListingId) {
        this.leftListingId = String(payload.leftListingId);
        this.rightListingId = String(payload.rightListingId);
        logger.log('📂 Loaded hero scene settings from database:', {
          left: this.leftListingId,
          right: this.rightListingId,
        });
      } else {
        logger.log('📂 No hero scene settings configured');
      }
    } catch (error) {
      console.error('❌ Failed to load hero scene settings:', error);
    } finally {
      this.loading = false;
    }
  }

  /**
   * Save settings to database
   * @param {string} leftId
   * @param {string} rightId
   * @returns {Promise<boolean>} Success status
   */
  async save(leftId, rightId) {
    this.leftListingId = leftId;
    this.rightListingId = rightId;

    try {
      const params = new URLSearchParams({ action: 'saveHeroSettings' });
      const response = await fetch(`/.netlify/functions/local-db?${params}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          leftListingId: leftId,
          rightListingId: rightId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save to database');
      }

      logger.log('✅ Hero scene settings saved to database:', { left: leftId, right: rightId });
      return true;
    } catch (error) {
      console.error('❌ Failed to save hero scene settings:', error);
      return false;
    }
  }

  /**
   * Clear settings from database
   * @returns {Promise<void>}
   */
  async clear() {
    this.leftListingId = null;
    this.rightListingId = null;

    try {
      const params = new URLSearchParams({ action: 'clearHeroSettings' });
      await fetch(`/.netlify/functions/local-db?${params}`, {
        method: 'POST',
      });
      logger.log('🗑️ Hero scene settings cleared from database');
    } catch (error) {
      console.error('❌ Failed to clear hero scene settings:', error);
    }
  }

  /**
   * Get the configured listing IDs (always fresh from database)
   * @returns {Promise<{leftListingId: string|null, rightListingId: string|null}>}
   */
  async getListingIds() {
    await this.load();
    return {
      leftListingId: this.leftListingId,
      rightListingId: this.rightListingId,
    };
  }

  /**
   * Check if both listings are configured
   * @returns {boolean}
   */
  isConfigured() {
    return !!this.leftListingId && !!this.rightListingId;
  }
}

const heroSceneSettings = new HeroSceneSettings();
export default heroSceneSettings;
